class ProductMediaMarquee extends HTMLElement {
  #animation;
  #duration = 0;
  #distance = 0;
  #resizeObserver;
  #mutationObserver;
  #pauseReasons = new Set();
  #setupFrame;
  #abortController;
  #dragState;
  #suppressClickUntil = 0;
  #momentumFrame;
  #activeDirection;
  #wheelState;
  #wheelEndTimer;

  #scheduleSetup(preserveProgress = true) {
    cancelAnimationFrame(this.#setupFrame);
    this.#setupFrame = requestAnimationFrame(() => this.#setup(preserveProgress));
  }

  connectedCallback() {
    this.#activeDirection = this.dataset.direction === 'right' ? 'right' : 'left';
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.#bindInteractions();
    this.#playVideos();
    this.#scheduleSetup(false);

    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(() => {
        if (this.#dragState || this.#momentumFrame || this.#wheelState) return;
        this.#scheduleSetup(true);
      });
      this.#resizeObserver.observe(this);
      const viewport = this.querySelector('[data-marquee-viewport]');
      if (viewport) this.#resizeObserver.observe(viewport);
    }

    this.#mutationObserver = new MutationObserver((mutations) => {
      const mediaChanged = mutations.some((mutation) =>
        mutation.type === 'childList' ||
        ['src', 'srcset', 'poster'].includes(mutation.attributeName)
      );
      if (!mediaChanged) return;

      this.#playVideos();
      this.#scheduleSetup(false);
    });
    this.#mutationObserver.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['src', 'srcset', 'poster'],
    });
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.#setupFrame);
    cancelAnimationFrame(this.#momentumFrame);
    clearTimeout(this.#wheelEndTimer);
    this.#animation?.cancel();
    this.#resizeObserver?.disconnect();
    this.#mutationObserver?.disconnect();
    this.#abortController?.abort();
  }

  #setup(preserveProgress = false) {
    const track = this.querySelector('[data-marquee-track]');
    const viewport = this.querySelector('[data-marquee-viewport]');
    const groups = this.querySelectorAll('[data-marquee-group]');
    if (!track || !viewport || groups.length < 2) return;

    const viewportWidth = viewport.getBoundingClientRect().width;
    if (viewportWidth <= 0) return;

    const mobile = window.matchMedia('(max-width: 749px)').matches;
    const styles = getComputedStyle(this);
    const widthProperty = mobile ? '--marquee-mobile-width' : '--marquee-desktop-width';
    const widthPercent = Number.parseFloat(styles.getPropertyValue(widthProperty)) || 100;
    this.style.setProperty('--marquee-item-width', `${viewportWidth * widthPercent / 100}px`);

    const previousProgress = preserveProgress && this.#animation && this.#duration
      ? (Number(this.#animation.currentTime) % this.#duration) / this.#duration
      : 0;

    this.#animation?.cancel();
    this.#animation = undefined;

    const autoplay = this.dataset.autoplay === 'true';
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!autoplay || reducedMotion) {
      track.style.transform = 'translate3d(0, 0, 0)';
      return;
    }

    const distance = Math.abs(groups[1].offsetLeft - groups[0].offsetLeft);
    if (!distance) return;
    this.#distance = distance;

    const speed = Math.max(10, Number(this.dataset.speed) || 30);
    this.#duration = distance / speed * 1000;
    const moveRight = this.#activeDirection === 'right';

    const keyframes = moveRight
      ? [
          { transform: `translate3d(${-distance}px, 0, 0)` },
          { transform: 'translate3d(0, 0, 0)' },
        ]
      : [
          { transform: 'translate3d(0, 0, 0)' },
          { transform: `translate3d(${-distance}px, 0, 0)` },
        ];

    this.#animation = track.animate(keyframes, {
      duration: this.#duration,
      iterations: Infinity,
      easing: 'linear',
    });

    if (previousProgress) {
      this.#animation.currentTime = previousProgress * this.#duration;
    }

    if (this.#pauseReasons.size) this.#animation.pause();
  }

  #trackPosition(track) {
    const transform = getComputedStyle(track).transform;
    if (!transform || transform === 'none') return 0;

    try {
      return new DOMMatrixReadOnly(transform).m41;
    } catch {
      const values = transform.match(/matrix(?:3d)?\((.+)\)/)?.[1]?.split(',').map(Number);
      return values?.length === 16 ? values[12] : values?.[4] || 0;
    }
  }

  #normalizedPosition(position) {
    if (!this.#distance) return 0;
    const wrapped = ((position % this.#distance) + this.#distance) % this.#distance;
    return wrapped === 0 ? 0 : wrapped - this.#distance;
  }

  #seekToPosition(position) {
    if (!this.#animation || !this.#duration || !this.#distance) return;

    const normalized = this.#normalizedPosition(position);
    const progress = this.#activeDirection === 'right'
      ? (normalized + this.#distance) / this.#distance
      : -normalized / this.#distance;
    this.#animation.currentTime = progress * this.#duration;
  }

  #resumeFromPosition(position) {
    this.#setup();
    this.#seekToPosition(position);
    this.#resume('pointer');
  }

  #playVideos() {
    this.querySelectorAll('video').forEach((video) => {
      video.muted = true;
      video.defaultMuted = true;
      video.loop = true;
      video.playsInline = true;
      video.play()?.catch(() => {});
    });
  }

  #startMomentum(position, velocity, directionDelta) {
    let currentPosition = position;
    if (Math.abs(directionDelta) > 0.5) {
      this.#activeDirection = directionDelta > 0 ? 'right' : 'left';
    } else if (Math.abs(velocity) > 0.01) {
      this.#activeDirection = velocity > 0 ? 'right' : 'left';
    }

    const marqueeSpeed = Math.max(10, Number(this.dataset.speed) || 30) / 1000;
    const autoplayVelocity = this.#activeDirection === 'right' ? marqueeSpeed : -marqueeSpeed;
    const releaseSpeed = Math.max(marqueeSpeed, Math.min(3, Math.abs(velocity)));
    let currentVelocity = this.#activeDirection === 'right' ? releaseSpeed : -releaseSpeed;
    let previousTime = performance.now();

    const step = (time) => {
      const track = this.querySelector('[data-marquee-track]');
      if (!track) {
        this.#momentumFrame = undefined;
        this.#resumeFromPosition(currentPosition);
        return;
      }

      const elapsed = Math.min(32, time - previousTime);
      previousTime = time;
      currentPosition = this.#normalizedPosition(currentPosition + currentVelocity * elapsed);
      track.style.transform = `translate3d(${currentPosition}px, 0, 0)`;
      const momentumRetention = Math.pow(0.95, elapsed / 16.67);
      currentVelocity = autoplayVelocity + (currentVelocity - autoplayVelocity) * momentumRetention;

      if (Math.abs(currentVelocity - autoplayVelocity) <= 0.005) {
        this.#momentumFrame = undefined;
        this.#resumeFromPosition(currentPosition);
        return;
      }

      this.#momentumFrame = requestAnimationFrame(step);
    };

    this.#momentumFrame = requestAnimationFrame(step);
  }

  #pause(reason) {
    this.#pauseReasons.add(reason);
    this.#animation?.pause();
  }

  #resume(reason) {
    this.#pauseReasons.delete(reason);
    if (!this.#pauseReasons.size) this.#animation?.play();
  }

  #bindInteractions() {
    const { signal } = this.#abortController;
    const viewport = this.querySelector('[data-marquee-viewport]');

    if (this.dataset.pauseHover === 'true') {
      this.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse') this.#pause('hover');
      }, { signal });
      this.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'mouse') this.#resume('hover');
      }, { signal });
    }

    this.addEventListener('focusin', (event) => {
      if (event.target.matches?.(':focus-visible')) this.#pause('focus');
    }, { signal });
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.#resume('focus');
    }, { signal });

    if (viewport) {
      viewport.addEventListener('wheel', (event) => {
        if (event.ctrlKey || this.dataset.autoplay !== 'true' || !this.#distance || this.#dragState) return;

        const horizontalDelta = Math.abs(event.deltaX) > Math.abs(event.deltaY)
          ? event.deltaX
          : event.shiftKey
            ? event.deltaY
            : 0;
        if (!horizontalDelta) return;

        event.preventDefault();
        const track = this.querySelector('[data-marquee-track]');
        if (!track) return;

        if (!this.#wheelState) {
          cancelAnimationFrame(this.#momentumFrame);
          this.#momentumFrame = undefined;
          const position = this.#trackPosition(track);
          this.#pauseReasons.delete('focus');
          this.#pause('pointer');
          this.#animation?.cancel();
          this.#animation = undefined;
          track.style.transform = `translate3d(${position}px, 0, 0)`;
          this.#wheelState = {
            position,
            velocity: 0,
            lastMovement: 0,
            lastTime: performance.now(),
            track,
          };
        }

        const wheel = this.#wheelState;
        const now = performance.now();
        const elapsed = Math.max(1, now - wheel.lastTime);
        const movement = -horizontalDelta;
        const instantaneousVelocity = movement / elapsed;
        wheel.velocity = wheel.velocity * 0.55 + instantaneousVelocity * 0.45;
        wheel.lastMovement = movement;
        wheel.lastTime = now;
        wheel.position = this.#normalizedPosition(wheel.position + movement);
        wheel.track.style.transform = `translate3d(${wheel.position}px, 0, 0)`;

        clearTimeout(this.#wheelEndTimer);
        this.#wheelEndTimer = setTimeout(() => {
          const completedWheel = this.#wheelState;
          this.#wheelState = undefined;
          if (!completedWheel) return;
          this.#startMomentum(
            completedWheel.position,
            completedWheel.velocity,
            completedWheel.lastMovement
          );
        }, 90);
      }, { passive: false, signal });

      viewport.addEventListener('pointerdown', (event) => {
        if (this.dataset.autoplay !== 'true' || !this.#distance) return;
        if (event.pointerType === 'mouse' && event.button !== 0) return;

        const track = this.querySelector('[data-marquee-track]');
        if (!track) return;

        clearTimeout(this.#wheelEndTimer);
        this.#wheelState = undefined;
        cancelAnimationFrame(this.#momentumFrame);
        this.#momentumFrame = undefined;
        const startPosition = this.#trackPosition(track);
        this.#pauseReasons.delete('focus');
        this.#pause('pointer');
        this.#animation?.cancel();
        this.#animation = undefined;
        track.style.transform = `translate3d(${startPosition}px, 0, 0)`;
        this.#dragState = {
          pointerId: event.pointerId,
          startX: event.clientX,
          startPosition,
          position: startPosition,
          track,
          lastX: event.clientX,
          lastTime: performance.now(),
          velocity: 0,
          lastMovement: 0,
          moved: false,
        };
        // Preserve the clicked media as the mouse click target.
        // Touch capture stays on the viewport, as in the working phone version.
        this.#dragState.captureTarget = event.pointerType === 'mouse'
          ? event.target.closest?.('[data-marquee-zoom-trigger]') || viewport
          : viewport;
        this.#dragState.captureTarget.setPointerCapture?.(event.pointerId);
        this.classList.add('is-dragging');
      }, { passive: true, signal });

      viewport.addEventListener('pointermove', (event) => {
        const drag = this.#dragState;
        if (!drag || drag.pointerId !== event.pointerId || !this.#distance) return;

        const deltaX = event.clientX - drag.startX;
        if (Math.abs(deltaX) > 4) drag.moved = true;

        const now = performance.now();
        const elapsed = Math.max(1, now - drag.lastTime);
        const movement = event.clientX - drag.lastX;
        const instantaneousVelocity = movement / elapsed;
        drag.velocity = drag.velocity * 0.65 + instantaneousVelocity * 0.35;
        if (Math.abs(movement) > 0.1) drag.lastMovement = movement;
        drag.lastX = event.clientX;
        drag.lastTime = now;

        drag.position = this.#normalizedPosition(drag.startPosition + deltaX);
        drag.track.style.transform = `translate3d(${drag.position}px, 0, 0)`;

        if (drag.moved) event.preventDefault();
      }, { passive: false, signal });

      const finishDrag = (event) => {
        const drag = this.#dragState;
        if (!drag || drag.pointerId !== event.pointerId) return;

        if (drag.moved) this.#suppressClickUntil = performance.now() + 500;
        this.#dragState = undefined;
        this.classList.remove('is-dragging');
        if (drag.captureTarget.hasPointerCapture?.(event.pointerId)) {
          drag.captureTarget.releasePointerCapture(event.pointerId);
        }
        const releaseDelay = performance.now() - drag.lastTime;
        const releaseVelocity = drag.velocity * Math.pow(0.85, releaseDelay / 16.67);
        const gestureDelta = event.clientX - drag.startX;
        const directionDelta = Math.abs(drag.lastMovement) > 0.5 ? drag.lastMovement : gestureDelta;
        if (event.type !== 'pointercancel' && drag.moved) {
          this.#startMomentum(drag.position, releaseVelocity, directionDelta);
        } else {
          this.#resumeFromPosition(drag.position);
        }

      };

      viewport.addEventListener('pointerup', finishDrag, { passive: true, signal });
      viewport.addEventListener('pointercancel', finishDrag, { passive: true, signal });
    }

    this.addEventListener('click', (event) => {
      if (event.target.closest?.('[data-marquee-zoom-action]')) return;

      const zoomTrigger = event.target.closest?.('[data-marquee-zoom-trigger]');
      if (!zoomTrigger) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      if (performance.now() <= this.#suppressClickUntil) {
        this.#suppressClickUntil = 0;
        return;
      }

      zoomTrigger.querySelector('[data-marquee-zoom-action]')?.click();
    }, { capture: true, signal });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.#pause('visibility');
      } else {
        this.#resume('visibility');
      }
    }, { signal });
  }
}

if (!customElements.get('product-media-marquee')) {
  customElements.define('product-media-marquee', ProductMediaMarquee);
}
